import React from 'react';
import { describeShortfall, fmtAed } from './walletShortfall';
import './walletShortfallPanel.css';

/**
 * THE ZERO-FLOOR PANEL (P3). Rendered instead of a red error whenever a spend
 * would push available below zero.
 *
 * It is a calm, informational block — not an error state. It leads with the
 * shortfall in money, explains what is holding the rest of the balance, and
 * offers the three resolving actions in a fixed order. `onPayByCard` is wired
 * to the existing Pay-now + wallet-toggle checkout so the card leg is the
 * natural continuation rather than a separate rail the client has to discover.
 *
 * Any handler the caller omits simply hides that action — the panel never
 * renders a button that does nothing.
 */
export default function WalletShortfallPanel({
    cost,
    available,
    totalBalance = null,
    committed = [],
    amountOwed = 0,
    shortfall = null,
    onPayByCard = null,
    onTopUp = null,
    onReduce = null,
    disabled = false,
    heading = 'Your wallet doesn’t cover this yet',
}) {
    const plan = describeShortfall({
        cost,
        available,
        totalBalance,
        committed,
        amountOwed,
        shortfall,
        canPayByCard: !!onPayByCard,
    });

    const handlers = { card: onPayByCard, topup: onTopUp, reduce: onReduce };
    const actions = plan.actions.filter((a) => typeof handlers[a.key] === 'function');

    return (
        <div className="wsp" role="status">
            <div className="wsp__head">
                <span className="wsp__icon" aria-hidden="true">👛</span>
                <div className="wsp__headText">
                    <div className="wsp__heading">{heading}</div>
                    <div className="wsp__assurance">{plan.assurance}</div>
                </div>
            </div>

            <div className="wsp__headline">{plan.headline}</div>

            {plan.rows.length > 0 && (
                <div className="wsp__rows">
                    {plan.rows.map((r) => (
                        <div className="wsp__row" key={r.label}>
                            <span>{r.label}{r.count > 0 ? ` · ${r.count}` : ''}</span>
                            <span>−{fmtAed(r.amount)}</span>
                        </div>
                    ))}
                </div>
            )}

            {plan.why && <div className="wsp__why">{plan.why}</div>}
            {plan.owedNote && <div className="wsp__owed">{plan.owedNote}</div>}

            {actions.length > 0 && (
                <div className="wsp__actions">
                    {actions.map((a) => (
                        <button
                            key={a.key}
                            type="button"
                            className={`wsp__btn ${a.primary ? 'wsp__btn--primary' : 'wsp__btn--ghost'}`}
                            onClick={handlers[a.key]}
                            disabled={disabled}
                        >
                            <span className="wsp__btnLabel">{a.label}</span>
                            <span className="wsp__btnHint">{a.hint}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
